import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Globe, Tag, type LucideIcon } from 'lucide-react';
import IconRight from '@/icons/IconRight.svg?react';
import ChevronDownIcon from '@/icons/ChevronDown.svg?react';
import ChevronUpIcon from '@/icons/ChevronUp.svg?react';
import ShareIcon from '@/icons/Share.svg?react';
import ResourceCatalogBlueIcon from '@/icons/ResourceCatalogBlue.svg?react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import type { Service } from '@/types/service-directory';

// Import GoldenBuckeye icon (will be rendered conditionally)
import GoldenBuckeyeIcon from '@/icons/GoldenBuckeye.svg?react';

/**
 * Props interface for ServiceDirectoryCard component
 */
export interface ServiceDirectoryCardProps {
  /** Service data */
  service: Service;
  /** Card number (1-indexed for display) */
  index: number;
  /** Whether the card is selected */
  isSelected?: boolean;
  /** Whether the service is resource saved */
  isSaved?: boolean;
  /** Subcategory names (resolved from IDs) */
  subcategoryNames: string[];
  /** Highlighted text from search (optional) */
  highlightedText?: string[];
  /** Callback when card is clicked */
  onClick?: () => void;
  /** Callback when share is clicked */
  onShare?: () => void;
  /** Callback when save resource is clicked */
  onSaveToCatalog?: () => void;
  /** Whether in tablet view (affects button layout) */
  isTablet?: boolean;
  /** Whether in mobile view (uses horizontal button layout like desktop) */
  isMobile?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Highlights matching text in a string
 */
function HighlightedText({
  text,
  highlights,
}: {
  text: string;
  highlights?: string[];
}) {
  if (!highlights || highlights.length === 0) {
    return <>{text}</>;
  }

  // Create a regex pattern from highlights
  const pattern = new RegExp(`(${highlights.map(h => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, index) => {
        const isHighlighted = highlights.some(
          (h) => h.toLowerCase() === part.toLowerCase()
        );
        return isHighlighted ? (
          <mark
            key={index}
            className="bg-[#FAFF69]"
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        );
      })}
    </>
  );
}

/**
 * ServiceDirectoryCard - Card displaying service information
 *
 * Displays a service with title, subcategories, contact information,
 * Golden Buckeye status, and action buttons.
 *
 * @example
 * ```tsx
 * import { ServiceDirectoryCard } from '@/components';
 *
 * <ServiceDirectoryCard
 *   service={service}
 *   index={1}
 *   isSelected={false}
 *   subcategoryNames={['Home-delivered meals', 'Food pantries']}
 *   onClick={() => handleSelect(service.id)}
 *   onSaveToCatalog={() => handleSave(service)}
 * />
 * ```
 */
export const ServiceDirectoryCard = forwardRef<
  HTMLDivElement,
  ServiceDirectoryCardProps
>(
  (
    {
      service,
      index,
      isSelected = false,
      isSaved = false,
      subcategoryNames,
      highlightedText,
      onClick,
      onShare,
      onSaveToCatalog,
      isTablet = false,
      isMobile = false,
      className,
    },
    ref
  ) => {
    const handleClick = useCallback(() => {
      onClick?.();
    }, [onClick]);

    const handleShareClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        onShare?.();
      },
      [onShare]
    );

    const handleSaveClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        wasClickedRef.current = true;
        onSaveToCatalog?.();
      },
      [onSaveToCatalog]
    );

    // Ref for save button focus restoration after state change (SHARED-DEFECT-005)
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const wasClickedRef = useRef(false);

    // Restore focus to save button after saved state changes when user clicked the button.
    // Uses requestAnimationFrame to run after all DOM updates and layout shifts
    // (e.g. catalog drawer opening) so focus isn't stolen by subsequent renders.
    useEffect(() => {
      if (wasClickedRef.current) {
        wasClickedRef.current = false;
        requestAnimationFrame(() => {
          saveButtonRef.current?.focus();
        });
      }
    }, [isSaved]);

    // State for expanded subcategory pills
    const [isPillsExpanded, setIsPillsExpanded] = useState(false);

    // Max pills to show when collapsed (2 pills total, then expand button on second line)
    const MAX_VISIBLE_PILLS = 2;
    // Total pills includes Golden Buckeye if applicable
    const totalPillsCount = subcategoryNames.length + (service.isGoldenBuckeye ? 1 : 0);
    const hasMorePills = totalPillsCount > MAX_VISIBLE_PILLS;
    // When collapsed, Golden Buckeye takes one slot if present
    const maxRegularPillsWhenCollapsed = service.isGoldenBuckeye
      ? Math.max(0, MAX_VISIBLE_PILLS - 1)
      : MAX_VISIBLE_PILLS;
    const visiblePills = isPillsExpanded
      ? subcategoryNames
      : subcategoryNames.slice(0, maxRegularPillsWhenCollapsed);
    const hiddenPillsCount = totalPillsCount - MAX_VISIBLE_PILLS;

    const handleTogglePills = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsPillsExpanded((prev) => !prev);
      },
      []
    );

    // Format address for display
    const formattedAddress = service.address
      ? `${service.address.street}, ${service.address.city}, ${service.address.state} ${service.address.zip}`
      : null;

    // Format address for Google Maps link
    const mapsUrl = service.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress!)}`
      : null;

    // Format county coverage label for county-wide services (no physical address)
    const countyLabel = (() => {
      if (service.hasPhysicalAddress !== false || !service.county || service.county.length === 0) {
        return null;
      }
      if (service.county.length === 1) {
        return `Serves ${service.county[0]} County`;
      }
      const last = service.county[service.county.length - 1];
      const rest = service.county.slice(0, -1).join(', ');
      return `Serves ${rest}, and ${last} Counties`;
    })();

    // Handle card click - only trigger if the click wasn't on a link or button
    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.closest('a') || target.closest('button')) {
        return; // Don't trigger onClick if clicking a link or button
      }
      handleClick();
    };

    // Handle keyboard activation - only if not focused on a link/button
    const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const activeElement = document.activeElement;
        if (activeElement?.tagName === 'A' || activeElement?.tagName === 'BUTTON') {
          return;
        }
        e.preventDefault();
        handleClick();
      }
    };

    return (
      <div
        ref={ref}
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        aria-label={`${service.title}. ${isSelected ? 'Selected.' : ''} Press Enter to ${isSelected ? 'deselect' : 'select'}.`}
        data-selected={isSelected}
        className={cn(
          'bg-white rounded p-4',
          'border-2 transition-colors duration-150',
          'shadow-[0px_4px_4px_0px_#BBBBBB40]',
          'motion-reduce:transition-none',
          'cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
          isSelected
            ? 'border-[var(--color-service-selected)]'
            : 'border-transparent hover:border-[var(--color-border-hover)]',
          className
        )}
      >
        {/* Title with number prefix */}
        <h3
          className={cn(
            typography.subtitle,
            'text-black mb-2'
          )}
        >
          <HighlightedText
            text={`${index}. ${service.title}`}
            highlights={highlightedText}
          />
        </h3>

        {/* Subcategory pills (including Golden Buckeye as first pill if applicable) */}
        {(subcategoryNames.length > 0 || service.isGoldenBuckeye) && (
          <div className="flex flex-wrap gap-1 mb-3">
            {/* Golden Buckeye pill - always first if applicable */}
            {service.isGoldenBuckeye && (
              <span
                className={cn(
                  'inline-flex items-center gap-1.5',
                  'h-8 py-1 px-3 rounded-full',
                  'max-w-full',
                  typography.body,
                  'bg-[var(--color-golden-buckeye-bg)] text-black',
                  'border border-[var(--color-golden-buckeye)]'
                )}
              >
                <GoldenBuckeyeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">Golden Buckeye Business</span>
              </span>
            )}
            {/* Regular subcategory pills - no expand needed */}
            {visiblePills.length > 0 && !hasMorePills && visiblePills.map((name, idx) => (
              <span
                key={idx}
                className={cn(
                  'inline-flex items-center',
                  'h-8 py-1 px-3 rounded-full',
                  'max-w-full',
                  typography.body,
                  'bg-white text-[#333333]',
                  'border border-[#D2D2D2]'
                )}
              >
                <span className="truncate">
                  <HighlightedText text={name} highlights={highlightedText} />
                </span>
              </span>
            ))}
            {/* When collapsed with expand button: show pills with last pill + button grouped */}
            {hasMorePills && !isPillsExpanded && (
              <>
                {/* First pills standalone */}
                {visiblePills.slice(0, -1).map((name, idx) => (
                  <span
                    key={idx}
                    className={cn(
                      'inline-flex items-center',
                      'h-8 py-1 px-3 rounded-full',
                      'max-w-full',
                      typography.body,
                      'bg-white text-[#333333]',
                      'border border-[#D2D2D2]'
                    )}
                  >
                    <span className="truncate">
                      <HighlightedText text={name} highlights={highlightedText} />
                    </span>
                  </span>
                ))}
                {/* Last pill + expand button grouped together */}
                <div className="flex items-center gap-1 max-w-full min-w-0">
                  <span
                    className={cn(
                      'inline-flex items-center',
                      'h-8 py-1 px-3 rounded-full',
                      'min-w-0',
                      typography.body,
                      'bg-white text-[#333333]',
                      'border border-[#D2D2D2]'
                    )}
                  >
                    <span className="truncate">
                      <HighlightedText text={visiblePills[visiblePills.length - 1]} highlights={highlightedText} />
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={handleTogglePills}
                    className={cn(
                      'inline-flex items-center gap-1',
                      'h-8 py-1 px-2 rounded-full',
                      'bg-[#F2F2F2] text-[#333333]',
                      'border border-[#D2D2D2]',
                      'cursor-pointer shrink-0',
                      'transition-colors duration-150',
                      'hover:bg-[#E8E8E8]',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2'
                    )}
                    aria-expanded={false}
                    aria-label={`Show ${hiddenPillsCount} more subcategories`}
                  >
                    <span className={cn(typography.buttonDefault, 'text-[#333333]')}>
                      +{hiddenPillsCount}
                    </span>
                    <ChevronDownIcon className="w-[10.8px] h-[6.8px] text-[#333333]" aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
            {/* When expanded: show all pills + collapse button on its own line */}
            {hasMorePills && isPillsExpanded && (
              <>
                {subcategoryNames.map((name, idx) => (
                  <span
                    key={idx}
                    className={cn(
                      'inline-flex items-center',
                      'h-8 py-1 px-3 rounded-full',
                      'max-w-full',
                      typography.body,
                      'bg-white text-[#333333]',
                      'border border-[#D2D2D2]'
                    )}
                  >
                    <span className="truncate">
                      <HighlightedText text={name} highlights={highlightedText} />
                    </span>
                  </span>
                ))}
                {/* Collapse button on its own line - perfect circle */}
                <div className="basis-full">
                  <button
                    type="button"
                    onClick={handleTogglePills}
                    className={cn(
                      'inline-flex items-center justify-center',
                      'w-8 h-8 rounded-full',
                      'bg-[#F2F2F2] text-[#333333]',
                      'border border-[#D2D2D2]',
                      'cursor-pointer shrink-0',
                      'transition-colors duration-150',
                      'hover:bg-[#E8E8E8]',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2'
                    )}
                    aria-expanded={true}
                    aria-label="Collapse subcategories"
                  >
                    <ChevronUpIcon className="w-[10.8px] h-[6.8px] text-[#333333]" aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Contact links */}
        <div className={cn('flex flex-col mb-3', isMobile ? 'gap-0' : 'gap-1.5')}>
          {/* County coverage label for county-wide services */}
          {countyLabel && (
            <div
              className={cn(
                'flex items-start gap-2',
                typography.buttonDefault,
                'text-[var(--color-text-secondary)]'
              )}
            >
              <MapPin className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{countyLabel}</span>
            </div>
          )}

          {/* Address */}
          {formattedAddress && (() => {
            const lastSpaceIndex = formattedAddress.lastIndexOf(' ');
            const addressStart = lastSpaceIndex > 0 ? formattedAddress.slice(0, lastSpaceIndex + 1) : '';
            const addressEnd = lastSpaceIndex > 0 ? formattedAddress.slice(lastSpaceIndex + 1) : formattedAddress;
            const hasNextItem = service.phone || service.website;
            return (
              <a
                href={mapsUrl!}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  'flex items-start gap-2',
                  typography.buttonDefault,
                  'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                  'hover:underline',
                  'transition-colors duration-150',
                  'motion-reduce:transition-none',
                  isMobile && 'min-h-11 py-2',
                  isMobile && hasNextItem && 'border-b border-[#EBEBEB]'
                )}
              >
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  {addressStart}
                  <span className="whitespace-nowrap">{addressEnd}<IconRight className="h-4 w-4 inline-block align-middle ml-1" aria-hidden="true" /></span>
                </span>
              </a>
            );
          })()}

          {/* Phone */}
          {service.phone && (() => {
            const hasNextItem = service.website;
            return (
              <a
                href={`tel:${service.phone}`}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  'flex items-start gap-2',
                  typography.buttonDefault,
                  'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                  'hover:underline',
                  'transition-colors duration-150',
                  'motion-reduce:transition-none',
                  isMobile && 'min-h-11 py-2',
                  isMobile && hasNextItem && 'border-b border-[#EBEBEB]'
                )}
              >
                <Phone className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="whitespace-nowrap">{service.phone}<IconRight className="h-4 w-4 inline-block align-middle ml-1" aria-hidden="true" /></span>
              </a>
            );
          })()}

          {/* Website */}
          {service.website && (
            <a
              href={service.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'flex items-start gap-2',
                typography.buttonDefault,
                'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                'hover:underline',
                'transition-colors duration-150',
                'motion-reduce:transition-none',
                isMobile && 'min-h-11 py-2'
              )}
            >
              <Globe className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="whitespace-nowrap">Website<IconRight className="h-4 w-4 inline-block align-middle ml-1" aria-hidden="true" /></span>
            </a>
          )}
        </div>

        {/* Golden Buckeye discount */}
        {service.isGoldenBuckeye && service.goldenBuckeyeDiscount && (
          <div
            className={cn(
              'flex items-center gap-1.5 mb-3',
              typography.body,
              'text-black'
            )}
          >
            <Tag className="w-4 h-4 shrink-0 text-[var(--color-primary-brand)]" aria-hidden="true" />
            {service.goldenBuckeyeDiscount}
          </div>
        )}

        {/* Action buttons */}
        {/* Tablet (not mobile): vertical layout with share below save */}
        {/* Desktop and Mobile: horizontal layout with share to left of save */}
        <div className={cn(
          'flex pt-2',
          isTablet && !isMobile ? 'flex-col items-center gap-2' : 'items-center justify-end gap-4'
        )}>
          {/* Share button - before Save in desktop and mobile */}
          {(!isTablet || isMobile) && (
            <button
              type="button"
              onClick={handleShareClick}
              className={cn(
                'inline-flex items-center justify-center',
                'cursor-pointer',
                'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                'transition-colors duration-150',
                'motion-reduce:transition-none',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
              )}
              aria-label="Share"
            >
              <ShareIcon className="w-6 h-6" aria-hidden="true" />
            </button>
          )}

          {/* Save resource button */}
          <Button
            ref={saveButtonRef}
            variant={isSaved ? 'primary' : 'secondary'}
            size="medium"
            content={{
              text: isSaved ? 'Resource saved' : 'Save resource',
              icon: ResourceCatalogBlueIcon as unknown as LucideIcon,
            }}
            onClick={handleSaveClick}
            aria-label={isSaved ? 'Resource saved' : 'Save resource'}
            aria-pressed={isSaved}
          />

          {/* Share button - below Save in tablet only (not mobile) */}
          {isTablet && !isMobile && (
            <button
              type="button"
              onClick={handleShareClick}
              className={cn(
                'inline-flex items-center justify-center',
                'cursor-pointer',
                'self-end',
                'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                'transition-colors duration-150',
                'motion-reduce:transition-none',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
              )}
              aria-label="Share"
            >
              <ShareIcon className="w-6 h-6" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  }
);

ServiceDirectoryCard.displayName = 'ServiceDirectoryCard';
